import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import { getPayablePoolTokenByAddress } from '@ui/constants/tokens';
import {
  calculateSpreadPercentage,
  getSpreadPercentageInfo,
} from '@ui/features/Spread/useSpreadPercentage';
import { toReadableValue, ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { ChainId } from '@ui/types/chain';
import { BareTradePair, TradePair } from '@ui/types/tradePair';
import { buildTradeKey } from '@ui/utils/buildObjectKey';
import { getClosePositionFee } from '@ui/utils/trade/getClosePositionFee';
import { getEstPnl } from '@ui/utils/trade/getEstPnl';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { getLiqPrice } from '@ui/utils/trade/getLiqPrice';
import { getMaxTpRatios } from '@ui/utils/trade/getMaxTpRatios';
import { getPositionNetValue } from '@ui/utils/trade/getPositionNetValue';
import { getPositionPnl } from '@ui/utils/trade/getPositionPnl';
import { getPositionPnlAfterFee } from '@ui/utils/trade/getPositionPnlAfterFee';
import { getRemainingCollateral } from '@ui/utils/trade/getRemainingCollateral';
import { getTakeProfitStopLossPercentage } from '@ui/utils/trade/getTakeProfitStopLossPercentage';
import { getTakeProfitStopLossPrice } from '@ui/utils/trade/getTakeProfitStopLossPrice';
import BigNumber from 'bignumber.js';
import { encodeAbiParameters, keccak256 } from 'viem';

import { ContractPosition, Position } from './types';

export const toPosition = ({
  position,
  chainId,
  pair,
  user,
  isPendingPosition = false,
}: {
  position: ContractPosition;
  chainId: ChainId;
  pair: BareTradePair;
  user?: `0x${string}`;
  isPendingPosition?: boolean;
}): Position => {
  const collateralToken = getPayablePoolTokenByAddress(
    position.marginToken,
    chainId,
  );
  if (!collateralToken) {
    throw new Error('Pay token missing in token list: ' + position.marginToken);
  }
  const tokenIn = position.tokenIn;
  const isLVTokenPay = tokenIn.toLowerCase() === collateralToken.address.toLowerCase();

  const size = toReadableValue(position.qty, PAIR_AMOUNT_DECIMALS); // pair amount
  const entryPrice = toReadableValue(position.entryPrice, USD_DECIMALS);
  const executionFee = toReadableValue(
    position.executionFee,
    collateralToken.decimals,
  );
  const collateral = toReadableValue(position.margin, collateralToken.decimals);
  const stopLoss = toReadableValue(position.stopLoss, USD_DECIMALS);
  const takeProfit = toReadableValue(position.takeProfit, USD_DECIMALS);
  const openFee = toReadableValue(position.openFee, collateralToken.decimals);
  // after multiplied -1, funding fee > 0 is paying, < 0 is earning, align with holding fee
  const fundingFee = toReadableValue(
    position.fundingFee,
    collateralToken.decimals,
  ).multipliedBy(-1);
  const holdingFee = toReadableValue(
    position.holdingFee,
    collateralToken.decimals,
  );
  const timestamp = position.timestamp;
  const expectedMergedPositionHash = user
    ? keccak256(
        encodeAbiParameters(
          [
            { name: 'user', type: 'address' },
            { name: 'pairBase', type: 'address' },
            { name: 'isLong', type: 'bool' },
            { name: 'lvToken', type: 'address' },
            { name: 'version', type: 'string' },
          ],
          [user, position.pairBase, position.isLong, position.marginToken, 'position.v1'],
        ),
      )
    : undefined;
  const isMergedPosition =
    expectedMergedPositionHash?.toLowerCase() ===
    position.positionHash.toLowerCase();

  const _getCustomTradeKey = (randomHexValue: string) => {
    return buildTradeKey({
      pairBase: position.pairBase,
      isLong: position.isLong,
      stopLoss: position.stopLoss,
      takeProfit: position.takeProfit,
      tokenIn: position.marginToken,
      randomHexValue,
    });
  };

  const _getLeverage: Position['getLeverage'] = ({
    collateralTokenMarketPrice,
  }) => {
    return getLeverage({
      collateralUsd: _getCollateralUsd(collateralTokenMarketPrice),
      sizeUsd: getSizeUsd(entryPrice),
    });
  };

  function getSizeUsd(pairMarketPrice: BigNumber): BigNumber {
    // pair amount * pairMarketPrice = positionSize
    return size.times(pairMarketPrice);
  }
  function _getCollateralUsd(collateralMarketPrice: BigNumber): BigNumber {
    return collateral.times(collateralMarketPrice);
  }

  const _getClosePositionFee: Position['getClosePositionFee'] = ({
    closeFeeP,
    minCloseFeeP,
    lvTokenDiscountP,
    shareP,
    pairMarketPrice,
    collateralMarketPrice,
    tradePair,
    override,
  }) => {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: getSizeUsd(pairMarketPrice), // use pairMarketPrice to calculate spread
      pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
    });

    return getClosePositionFee({
      isDegen: pair.isDegenPair,
      closeFeeP,
      lvTokenDiscountP,
      minCloseFeeP,
      shareP,
      isLVTokenPay,
      collateralUsd:
        override?.collateralUsd ?? _getCollateralUsd(collateralMarketPrice), // this should be before funding / holding fee, but not include open position fees
      fundingFee: getFundingFeeUsd(collateralMarketPrice),
      holdingFee: getHoldingFeeUsd(collateralMarketPrice),
      positionSize: getSizeUsd(pairMarketPrice),
      pnl: getPositionPnl({
        closePrice: pairMarketPrice,
        entryPrice,
        isLong: position.isLong,
        size,
        spreadPercentage: calculateSpreadPercentage({
          isLong: position.isLong,
          isClosePrice: true,
          spreadPercentageInfo,
        }),
      }),
    });
  };
  const _getPnl: Position['getPnl'] = ({ pairMarketPrice, tradePair }) => {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: getSizeUsd(pairMarketPrice), // use pairMarketPrice to calculate spread
      pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
    });

    return isPendingPosition
      ? ZERO_BIG_NUMBER
      : getPositionPnl({
          closePrice: pairMarketPrice,
          entryPrice,
          isLong: position.isLong,
          size,
          spreadPercentage: calculateSpreadPercentage({
            isLong: position.isLong,
            isClosePrice: true,
            spreadPercentageInfo,
          }),
        });
  };
  function getExecutionFeeUsd(collateralTokenMarketPrice: BigNumber) {
    return executionFee.times(collateralTokenMarketPrice);
  }
  function getFundingFeeUsd(collateralTokenMarketPrice: BigNumber) {
    return fundingFee.times(collateralTokenMarketPrice);
  }
  function getHoldingFeeUsd(collateralTokenMarketPrice: BigNumber) {
    return holdingFee.times(collateralTokenMarketPrice);
  }
  function getOpenFeeUsd(collateralTokenMarketPrice: BigNumber) {
    return openFee.times(collateralTokenMarketPrice);
  }

  function _getCollateralUsdBeforeOpenPositionFees(
    collateralTokenMarketPrice: BigNumber,
  ) {
    return _getCollateralUsd(collateralTokenMarketPrice)
      .plus(getExecutionFeeUsd(collateralTokenMarketPrice))
      .plus(getOpenFeeUsd(collateralTokenMarketPrice));
  }

  function _getPnlAfterFeePercentage({
    closeFeeP,
    lvTokenDiscountP,
    minCloseFeeP,
    shareP,
    pairMarketPrice,
    collateralMarketPrice,
    tradePair,
  }: {
    closeFeeP: number;
    lvTokenDiscountP: number;
    minCloseFeeP: number;
    shareP: number;
    pairMarketPrice: BigNumber;
    collateralMarketPrice: BigNumber;
    tradePair: TradePair;
  }) {
    const pnlAfterFee = getPositionPnlAfterFee({
      closeFee: _getClosePositionFee({
        closeFeeP,
        lvTokenDiscountP,
        minCloseFeeP,
        shareP,
        collateralMarketPrice,
        pairMarketPrice,
        tradePair,
      }),
      fundingFee: getFundingFeeUsd(collateralMarketPrice),
      holdingFee: getHoldingFeeUsd(collateralMarketPrice),
      pnl: _getPnl({ pairMarketPrice, tradePair }),
    });

    // const remainingCollateral = getRemainingCollateral({
    //   collateral,
    //   fundingFee,
    //   holdingFee,
    //   collateralPrice: collateralMarketPrice,
    // });

    return pnlAfterFee.div(_getCollateralUsd(collateralMarketPrice));
  }

  function _getTpSlPercentage({
    collateralTokenMarketPrice,
    targetPrice,
    pairMarketPrice,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    targetPrice: number;
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: getSizeUsd(BigNumber(pairMarketPrice)), // use targetPrice to calculate spread
      pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
    });

    return getTakeProfitStopLossPercentage({
      targetPrice: targetPrice,
      entryPrice: entryPrice.toNumber(),
      leverage: _getLeverage({
        collateralTokenMarketPrice,
      }).toNumber(),
      isLong: position.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: position.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  function _getTakeProfitPercentage({
    collateralTokenMarketPrice,
    pairMarketPrice,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    tradePair: TradePair;
  }) {
    return _getTpSlPercentage({
      collateralTokenMarketPrice,
      pairMarketPrice,
      targetPrice: takeProfit.toNumber(),
      tradePair,
    });
  }

  function _getStopLossPercentage({
    collateralTokenMarketPrice,
    pairMarketPrice,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    tradePair: TradePair;
  }) {
    return _getTpSlPercentage({
      collateralTokenMarketPrice,
      pairMarketPrice,
      targetPrice: stopLoss.toNumber(),
      tradePair,
    });
  }

  function _getTakeProfitDeltaPnl({
    // get current position take profit delta pnl
    collateralTokenMarketPrice,
    pairMarketPrice,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: getSizeUsd(takeProfit), // use takeProfit to calculate spread
      pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
    });

    return getEstPnl({
      collateralUsd: _getCollateralUsd(collateralTokenMarketPrice),
      targetPercentage: _getTakeProfitPercentage({
        collateralTokenMarketPrice,
        pairMarketPrice,
        tradePair,
      }),
      isLong: position.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: position.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  function _getStopLossDeltaPnl({
    // get current position stop loss delta pnl
    collateralTokenMarketPrice,
    pairMarketPrice,
    tradePair,
  }: {
    collateralTokenMarketPrice: BigNumber;
    pairMarketPrice: BigNumber;
    tradePair: TradePair;
  }) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: getSizeUsd(stopLoss), // use pairMarketPrice to calculate spread
      pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
    });

    return getEstPnl({
      collateralUsd: _getCollateralUsd(collateralTokenMarketPrice),
      targetPercentage: _getStopLossPercentage({
        collateralTokenMarketPrice,
        pairMarketPrice,
        tradePair,
      }),
      isLong: position.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: position.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  function _getMaxTpRatios({
    collateralTokenMarketPrice,
    maxTakeProfitP,
    currentPairMaxTpRatios,
  }: Parameters<Position['getMaxTpRatios']>[0]) {
    return getMaxTpRatios({
      leverage: _getLeverage({
        collateralTokenMarketPrice,
      }).toNumber(),
      maxTakeProfitP: maxTakeProfitP,
      currentPairMaxTpRatios: currentPairMaxTpRatios,
    });
  }

  function _getMaxTpRatiosPrice({
    collateralTokenMarketPrice,
    maxTakeProfitP,
    currentPairMaxTpRatios,
    pairMarketPrice,
    tradePair,
  }: Parameters<Position['getMaxTpRatiosPrice']>[0]) {
    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: getSizeUsd(pairMarketPrice), // use pairMarketPrice to calculate spread
      pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
    });

    return getTakeProfitStopLossPrice({
      pnlRate: _getMaxTpRatios({
        collateralTokenMarketPrice,
        maxTakeProfitP,
        currentPairMaxTpRatios,
      }),
      isTakeProfit: true,
      entryPrice: entryPrice.toNumber(),
      leverage: _getLeverage({
        collateralTokenMarketPrice,
      }).toNumber(),
      isLong: position.isLong,
      spreadPercentage: calculateSpreadPercentage({
        isLong: position.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      }),
    });
  }

  return {
    isPendingPosition,
    isMergedPosition,
    isLong: position.isLong,
    id: position.positionHash,
    pair,
    collateral,
    collateralToken,
    size,
    timestamp,
    entryPrice,
    stopLoss,
    takeProfit,
    openFee,
    executionFee,
    fundingFee,
    holdingFee,
    getExecutionFeeUsd,
    getFundingFeeUsd,
    getHoldingFeeUsd,
    getOpenFeeUsd,
    getNetValue({
      pairMarketPrice,
      collateralMarketPrice,
      closeFeeP,
      lvTokenDiscountP,
      minCloseFeeP,
      shareP,
      tradePair,
    }) {
      return getPositionNetValue({
        pnl: _getPnl({ pairMarketPrice, tradePair }),
        collateralAfterFee: _getCollateralUsd(collateralMarketPrice),
        closeFee: _getClosePositionFee({
          collateralMarketPrice,
          closeFeeP,
          lvTokenDiscountP,
          minCloseFeeP,
          shareP,
          pairMarketPrice,
          tradePair,
        }),
        fundingFee: getFundingFeeUsd(collateralMarketPrice),
        holdingFee: getHoldingFeeUsd(collateralMarketPrice),
      });
    },
    getPnl({ pairMarketPrice, tradePair }) {
      const spreadPercentageInfo = getSpreadPercentageInfo({
        tradePair,
        positionSize: getSizeUsd(pairMarketPrice), // use pairMarketPrice to calculate spread
        pairPrice: pairMarketPrice, // for position use pairMarketPrice to calculate spread
      });

      return getPositionPnl({
        closePrice: pairMarketPrice,
        entryPrice,
        isLong: position.isLong,
        size,
        spreadPercentage: calculateSpreadPercentage({
          isLong: position.isLong,
          isClosePrice: true,
          spreadPercentageInfo,
        }),
      });
    },
    getPnlAfterFee({
      closeFeeP,
      lvTokenDiscountP,
      minCloseFeeP,
      shareP,
      pairMarketPrice,
      collateralMarketPrice,
      tradePair,
    }) {
      return getPositionPnlAfterFee({
        closeFee: _getClosePositionFee({
          closeFeeP,
          lvTokenDiscountP,
          minCloseFeeP,
          shareP,
          collateralMarketPrice,
          pairMarketPrice,
          tradePair,
        }),
        fundingFee: getFundingFeeUsd(collateralMarketPrice),
        holdingFee: getHoldingFeeUsd(collateralMarketPrice),
        pnl: _getPnl({ pairMarketPrice, tradePair }),
      });
    },
    getLiquidationPrice({
      collateralMarketPrice,
      liqLostP,
      feeConfig,
      pairMarketPrice,
      tradePair,
      override,
    }) {
      const spreadPercentageInfo = getSpreadPercentageInfo({
        tradePair,
        positionSize: size.times(pairMarketPrice),
        pairPrice: pairMarketPrice,
      });

      const slippageP = calculateSpreadPercentage({
        isLong: position.isLong,
        isClosePrice: true,
        spreadPercentageInfo,
      });

      return getLiqPrice({
        positionQty: size,
        entryPrice,
        isLong: position.isLong,
        liqLostP,
        slippageP,
        countableCollateralUsd:
          override?.collateralUsd ?? _getCollateralUsd(collateralMarketPrice),
        fundingFee: getFundingFeeUsd(collateralMarketPrice),
        holdingFee: getHoldingFeeUsd(collateralMarketPrice),
        closeFee: _getClosePositionFee({
          ...feeConfig,
          collateralMarketPrice,
          pairMarketPrice,
          tradePair,
          override: {
            collateralUsd:
              override?.collateralUsd ??
              _getCollateralUsd(collateralMarketPrice),
          },
        }),
      });
    },
    getLeverage: _getLeverage,
    getSizeUsd: getSizeUsd,
    getCollateralUsd: _getCollateralUsd,
    getClosePositionFee: _getClosePositionFee,
    getRemainingCollateral({ collateralTokenMarketPrice }) {
      return getRemainingCollateral({
        collateral,
        fundingFee,
        holdingFee,
        collateralPrice: collateralTokenMarketPrice,
      });
    },
    getTakeProfitPercentage({
      collateralTokenMarketPrice,
      pairMarketPrice,
      tradePair,
    }) {
      return _getTakeProfitPercentage({
        collateralTokenMarketPrice,
        pairMarketPrice,
        tradePair,
      });
    },
    getTakeProfitDeltaPnl({
      collateralTokenMarketPrice,
      pairMarketPrice,
      tradePair,
    }) {
      return _getTakeProfitDeltaPnl({
        collateralTokenMarketPrice,
        pairMarketPrice,
        tradePair,
      });
    },
    getMaxTpRatios: _getMaxTpRatios,
    getMaxTpRatiosPrice: _getMaxTpRatiosPrice,
    getStopLossPercentage({
      collateralTokenMarketPrice,
      pairMarketPrice,
      tradePair,
    }) {
      return _getStopLossPercentage({
        collateralTokenMarketPrice,
        pairMarketPrice,
        tradePair,
      });
    },
    getStopLossDeltaPnl({
      collateralTokenMarketPrice,
      pairMarketPrice,
      tradePair,
    }) {
      return _getStopLossDeltaPnl({
        collateralTokenMarketPrice,
        pairMarketPrice,
        tradePair,
      });
    },
    getCollateralUsdBeforeOpenPositionFees(collateralTokenMarketPrice) {
      return _getCollateralUsdBeforeOpenPositionFees(
        collateralTokenMarketPrice,
      );
    },
    getPnlAfterFeePercentage({
      closeFeeP,
      lvTokenDiscountP,
      minCloseFeeP,
      shareP,
      pairMarketPrice,
      collateralMarketPrice,
      tradePair,
    }) {
      return _getPnlAfterFeePercentage({
        closeFeeP,
        lvTokenDiscountP,
        minCloseFeeP,
        shareP,
        pairMarketPrice,
        collateralMarketPrice,
        tradePair,
      });
    },
    getCustomTradeKey: _getCustomTradeKey,
  };
};
